import {getTestProps} from '@benchling/js-test-utils';
import {localPoint} from '@visx/event';
import {Group} from '@visx/group';
import React from 'react';

import {HeatmapAxisScaleType, HeatmapInternalPoint, HeatmapPoint} from '../Heatmap';
import invertBandScale from './utils/invertBandScale';

export interface HeatmapEventInfo<DataPoint extends HeatmapPoint> {
  // The point used for plotting, which may be transformed to fill in gaps or normalize labels
  dataPoint: HeatmapInternalPoint<DataPoint>;
  // The height of the bin being targeted
  targetBinHeight: number;
  // The width of the bin being targeted
  targetBinWidth: number;
  // The x position of the bin being targeted, relative to the HeatmapRect
  x: number;
  // The y position of the bin being targeted, relative to the HeatmapRect
  y: number;
}

export type HeatmapMouseEventHandler<DataPoint extends HeatmapPoint> = (
  event: HeatmapEventInfo<DataPoint> | null
) => void;

export function getHeatmapEventInfo<DataPoint extends HeatmapPoint>(
  mousePosition: {x: number; y: number} | null,
  xScale: HeatmapAxisScaleType,
  yScale: HeatmapAxisScaleType,
  points: Array<HeatmapInternalPoint<DataPoint>>,
  offsetPosition: {top: number; left: number}
): HeatmapEventInfo<DataPoint> | null {
  if (mousePosition == null) {
    return null;
  }

  const dataPoint = points.find(
    (point) =>
      String(point.x) === invertBandScale(xScale, mousePosition.x - offsetPosition.left) &&
      String(point.y) === invertBandScale(yScale, mousePosition.y - offsetPosition.top)
  );

  if (dataPoint == null) {
    return null;
  }

  return {
    dataPoint,
    targetBinHeight: yScale.step(),
    targetBinWidth: xScale.step(),
    x: xScale(dataPoint.x as number | string) ?? 0,
    y: yScale(dataPoint.y as number | string) ?? 0,
  };
}

export function createHeatmapMouseEventHandler<
  Event extends React.MouseEvent,
  DataPoint extends HeatmapPoint,
>({
  handler,
  xScale,
  yScale,
  points,
  offsetPosition,
}: {
  handler?: (info: HeatmapEventInfo<DataPoint> | null) => void;
  xScale: HeatmapAxisScaleType;
  yScale: HeatmapAxisScaleType;
  points: Array<HeatmapInternalPoint<DataPoint>>;
  offsetPosition: {top: number; left: number};
}): ((event: Event) => void) | undefined {
  return handler == null
    ? undefined
    : (event: Event) => {
        const mousePosition = localPoint(event);
        const positionInfo = getHeatmapEventInfo(mousePosition, xScale, yScale, points, offsetPosition);
        handler(positionInfo);
      };
}

export interface HeatmapWrapperProps<DataPoint extends HeatmapPoint> {
  children: React.ReactNode;
  offsetPosition: {top: number; left: number};
  points: Array<HeatmapInternalPoint<HeatmapPoint>>;
  xScale: HeatmapAxisScaleType;
  yScale: HeatmapAxisScaleType;
  onClickPoint?: HeatmapMouseEventHandler<DataPoint>;
  onMouseMove?: HeatmapMouseEventHandler<DataPoint>;
  onMouseLeave?: HeatmapMouseEventHandler<DataPoint>;
}

/**
 * Wrapper component for HeatmapRect used to translate difficult to work with raw mouse events into
 * bin-specific HeatmapEventInfo objects.
 *
 * Adding event handlers to this wrapper component is more efficient than defining the handlers on each
 * HeatmapBox since events can be handled for empty cells without rendering a HeatmapBox.
 *
 * The children should include a HeatmapRect. To be aware of the bins' positions, HeatmapWrapper
 * accepts an explicit offsetPosition and positions the children within itself.
 */
export default function HeatmapWrapper<DataPoint extends HeatmapPoint>({
  children,
  offsetPosition,
  points,
  xScale,
  yScale,
  onClickPoint,
  onMouseLeave,
  onMouseMove,
}: HeatmapWrapperProps<DataPoint>): JSX.Element {
  const createHandlerProps = {
    offsetPosition,
    points,
    xScale,
    yScale,
  };
  return (
    <Group
      left={offsetPosition.left}
      top={offsetPosition.top}
      onClick={createHeatmapMouseEventHandler({...createHandlerProps, handler: onClickPoint})}
      onMouseLeave={createHeatmapMouseEventHandler({...createHandlerProps, handler: onMouseLeave})}
      onMouseMove={createHeatmapMouseEventHandler({...createHandlerProps, handler: onMouseMove})}
      {...getTestProps({component: 'HeatmapWrapper', element: 'root'})}
    >
      {children}
    </Group>
  );
}
